import type { UnknownAction } from '@reduxjs/toolkit'
import { FETCHING_STREAM_REQUEST, FETCHING_STREAM_SUCCEEDED, FETCHING_STREAM_FAILED, FETCHING_STREAM_CHILDREN_REQUEST, FETCHING_STREAM_CHILDREN_SUCCEEDED, FETCHING_STREAM_CHILDREN_FAILED, FETCHING_STREAM_ANALYTICS_REQUEST, FETCHING_STREAM_ANALYTICS_SUCCEEDED, FETCHING_STREAM_ANALYTICS_FAILED, DELETING_STREAM_REQUEST, DELETING_STREAM_SUCCEEDED, DELETING_STREAM_FAILED, UPDATING_ASSET_REQUEST, UPDATING_ASSET_SUCCEEDED, UPDATING_ASSET_FAILED, UPDATING_GROUP_REQUEST, UPDATING_GROUP_SUCCEEDED, UPDATING_GROUP_FAILED, SORTING_STREAM, FETCHING_ASSET_ANALYTICS_SUCCEEDED, DELETING_ASSET_FROM_STREAM_REQUEST, DELETING_ASSET_FROM_STREAM_FAILED, DELETING_ASSET_FROM_STREAM_SUCCEEDED, DELETING_GROUP_REQUEST, DELETING_GROUP_SUCCEEDED, DELETING_GROUP_FAILED, MOVING_ENTITY_SUCCEEDED, UPDATING_STREAM_REQUEST, UPDATING_STREAM_SUCCEEDED, UPDATING_STREAM_FAILED, STREAM_UPDATE_PROPERTY, ADD_ASSET_TO_STREAM, ADD_ASSET_TO_STREAM_SUCCEDDED, ADD_ASSET_TO_STREAM_FAILED, CREATING_GROUP_IN_STREAM, CREATING_GROUP_IN_STREAM_SUCCEEDED, CREATING_GROUP_IN_STREAM_FAILED, CREATING_STREAM, CREATING_STREAM_SUCCEEDED, CREATING_STREAM_FAILED, INVALIDATE_STREAM_CREATION, FETCHING_ASSET_ANALYTICS_REQUEST, REPLACE_SIMILAR_ASSET_SUCCEEDED, UPDATING_STREAM_ACCESS_SUCCEDDED, INVALIDATE_STREAM_CHILDREN } from '../actions/actionTypes'
import _ from 'lodash';
import { FetchStreamResponse, FetchStreamError, FetchStreamChildrenResponse, FetchStreamAnalytics, FetchStreamAnalyticsError, FetchStreamAnalyticsResponse, FetchStreamChildrenError, DeleteStreamResponse, DeleteStreamError, DeleteAssetFromStreamSucceeded, DeleteAssetFromStreamFailed, FetchStreamChildrenRequest, AddAssetToStreamSucceeded, AddAssetToStreamFailed, CreateGroup, CreateGroupSucceeded, CreateGroupFailed, AddAssetToStream, CreateStreamError } from '../../types/streams';
import { FetchAssetAnalyticsRequest, FetchAssetAnalyticsResponse, ReplaceSimilarAssetResponse, UpdateAssetErrorResponse, UpdateAssetResponse } from '../../types/assets';
import { UpdateGroupFailed, UpdateGroupSucceeded } from '../../types/groups';
import {Stream as StreamType} from '../../types/streams';
import { getStreamSortPreference } from 'src/services/stream.helper';

interface StreamItem {
    loading: boolean;
    invalidated: boolean;
    item: (StreamType & AnalyticsState) | null;
    loadingChildren: boolean;
    fetchedChildren: boolean;
    subscribing: boolean;
    updatingGroup: boolean;
    updatingAsset: boolean;
    creatingSection: boolean;
    creatingAsset: boolean;
    createdAsset: boolean;
    hasMore: boolean;
    errors: any | null;
    deletion: StreamDeletionState;
    updation: StreamUpdateState;
    sort: {
      type: string;
      order: string;
    },
    pagination: {
      currentPage: number;
      totalPages: number;
      hasMoreItems: boolean;
    },
    assetsDeletion: AssetsDeletion;
    groupsDeletion: GroupsDeletion;
    edit: StreamEditState | null;
    groupCreation: {
      creating: boolean;
      created: boolean;
      item: any;
    }
  }
interface Stream {
  creation: {
    item: StreamType | null;
    creating: boolean;
    created: boolean;
    error: any;
  }
  items: {
    [key: string]: StreamItem;
  };
}

interface StreamDeletionState {
  deleting: boolean,
  deleted: boolean,
  error?: any
}

interface StreamUpdateState {
  updating: boolean,
  updated: boolean,
  error: any
}

interface StreamEditState {
  [property: string]: any;
}

interface AssetsDeletion {
  deleting: boolean,
  deleted: boolean
}

interface GroupsDeletion {
  deleting: boolean,
  deleted: boolean
}

interface AnalyticsState {
  loading: boolean,
  item: {
    views: number | null;
    shares: number | null;
    downloads: number | null;
    timeSpent: number | null;
  }
}

interface CreateStreamState {
  item: StreamType | null;
  creating: boolean;
  created: boolean;
  error: any;
}

const initialStreamDeletionState: StreamDeletionState = {
  deleting: false,
  deleted: false,
  error: null
}

const initialStreamUpdateState: StreamUpdateState = {
  updating: false,
  updated: false,
  error: null
}

const initialAssetsDeletion: AssetsDeletion = {
  deleting: false,
  deleted: false
}

const initialGroupsDeletion: GroupsDeletion = {
  deleting: false,
  deleted: false
}

const initialAnalyticsState: AnalyticsState = {
  loading: false,
  item: {
    views: null,
    shares: null,
    downloads: null,
    timeSpent: null
  },
}

const initialCreateStreamState: CreateStreamState = {
    item: null,
    creating: false,
    created: false,
    error: null
}

const initialState: Stream = {
  items: {},
  creation: initialCreateStreamState,
};

interface FetchStreamsAction {
  [key: string]: any;
}

function sortChildren(type: string, order: string, items: any[]) {
  var sortedGroup = items.filter((item) => {
    return item.entityType === 'group';
  });

  var sortedAsset = items.filter((item) => {
    return item.entityType === 'asset';
  });

  const compareByName = (a: any, b: any) => {
    const nameA = a.name ? a.name.toLowerCase() : '';
    const nameB = b.name ? b.name.toLowerCase() : '';

    const numA = parseFloat(nameA);
    const numB = parseFloat(nameB);

    if (!isNaN(numA) && !isNaN(numB)) {
      return order === "ASC" ? numA - numB : numB - numA;
    } else {
      return order === "ASC" ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
    }
  };

  if (type === "name") {
    sortedGroup.sort(compareByName);
  }

  if (type === 'created') {
    sortedGroup.sort((a: any, b: any) => {
      const nameA = ((a.createdBy?.firstName ?? '') + ' ' + (a.createdBy?.lastName ?? '')).trim().toLowerCase();
      const nameB = ((b.createdBy?.firstName ?? '') + ' ' + (b.createdBy?.lastName ?? '')).trim().toLowerCase();
      return order === 'ASC'
        ? nameA.localeCompare(nameB)
        : nameB.localeCompare(nameA);
    });

    sortedAsset.sort((a: any, b: any) => {
      const nameA = ((a.author?.firstName ?? '') + ' ' + (a.author?.lastName ?? '')).trim().toLowerCase();
      const nameB = ((b.author?.firstName ?? '') + ' ' + (b.author?.lastName ?? '')).trim().toLowerCase();
      return order === 'ASC'
        ? nameA.localeCompare(nameB)
        : nameB.localeCompare(nameA);
    });
  }

  else if (type === 'modified_date') {
    sortedGroup.sort((a: any, b: any) => {
      const aTime = new Date(a.modifiedDate).getTime();
      const bTime = new Date(b.modifiedDate).getTime();
      return order === 'ASC' ? aTime - bTime : bTime - aTime;
    });

    sortedAsset.sort((a: any, b: any) => {
      const aTime = new Date(a.modifiedDate).getTime();
      const bTime = new Date(b.modifiedDate).getTime();
      return order === 'ASC' ? aTime - bTime : bTime - aTime;
    });
  }

  if (type !== "name") {
    sortedAsset.sort((a: any, b: any) => {
      var analyticsA = (a.analytics) || {};
      var analyticsB = (b.analytics) || {};

      let valueA = 0, valueB = 0;

      if (type === "views") {
        valueA = analyticsA.views || 0;
        valueB = analyticsB.views || 0;
      } else if (type === "shares") {
        valueA = analyticsA.shares || 0;
        valueB = analyticsB.shares || 0;
      } else if (type === "downloads") {
        valueA = analyticsA.downloads || 0;
        valueB = analyticsB.downloads || 0;
      } else if (type === "timeSpent") {
        valueA = analyticsA.avgTimeSpent || 0;
        valueB = analyticsB.avgTimeSpent || 0;
      }

      if (order === "ASC") {
        return valueA - valueB;
      } else {
        return valueB - valueA;
      }
    });
  } else {
    sortedAsset.sort(compareByName);
  }

  return [...sortedGroup, ...sortedAsset];
}


export function streamsReducer(state: Stream = initialState, action: FetchStreamsAction): Stream {
  switch (action.type) {
    case FETCHING_STREAM_REQUEST: {
      let data = action.payload;
      const id = data.id;
      const stream = state.items[id];

      return {
        ...state,
        items: {
          ...state.items,
          [id]: {
            ...stream,
            loading: true,
            invalidated: true,
          },
        },
      };
    }

    case FETCHING_STREAM_SUCCEEDED: {
      const data = action.payload as FetchStreamResponse;

      const id = data.stream.id;
      const stream = state.items[id];

      const children = stream.item ? (stream.item.children ?? []) : [];

      let analytics = stream.item?.analytics || { loading: false, item: null };
      if (data.stream.analytics) {
        analytics = { ...analytics, ...data.stream.analytics };
      }

      return {
        ...state,
        items: {
          ...state.items,
          [id]: {
            ...stream,
            loading: false,
            invalidated: false,
            item: {
              ...data.stream,
              children: children,
              analytics: {
                ...analytics,
              },
            },
          },
        },
      } as Stream;
    }

    case FETCHING_STREAM_FAILED: {
      const data = action.payload as FetchStreamError;
      const id = data.id;
      const stream = state.items[id];

      return {
        ...state,
        items: {
          ...state.items,
          [id]: {
            ...stream,
            loading: false,
            invalidated: false,
            item: null,
            errors: data.error,
          },
        },
      };
    }

    case FETCHING_STREAM_CHILDREN_REQUEST: {
      const { id } = action.payload as FetchStreamChildrenRequest;
      const stream = state.items[id];

      return {
        ...state,
        items: {
          ...state.items,
          [id]: {
            ...stream,
            loadingChildren: true,
          },
        },
      };
    }

    case FETCHING_STREAM_CHILDREN_SUCCEEDED: {
      const data = action.payload as FetchStreamChildrenResponse;
      const id = data.id;
      const stream = state.items[id];

      let children = action.payload.children.records;
      const streamChildren = _.get(stream, "item.children") || [];

      const { currentPage, totalPages } = data.children;

      if (currentPage != 0) {
        children = [...streamChildren, ...children];
      }

      children = _.uniqBy(children, "id").map((newChild: any) => {
        const existingChild = streamChildren.find(
          (child: any) => child.id === newChild.id,
        );
        return {
          ...newChild,
          analytics: existingChild?.analytics || newChild.analytics || null,
        };
      });

      const sortPreference = getStreamSortPreference();

      const sortedChildren = sortPreference &&
      sortPreference.sortBy !== 'name' &&
      sortPreference.sortBy !== 'modified_date'
      ? sortChildren(sortPreference.sortBy, sortPreference.orderBy, children)
      : children;

      return {
        ...state,
        items: {
          ...state.items,
          [id]: {
            ...stream,
            loadingChildren: false,
            fetchedChildren: true,
            item: {
              ...(stream.item || {}),
              children: sortedChildren,
            },
            pagination: {
              currentPage: currentPage,
              totalPages: totalPages,
              hasMoreItems: currentPage < totalPages - 1,
            },
            sort: action.payload.sort,
          },
        },
      } as Stream;
    }

    case FETCHING_STREAM_CHILDREN_FAILED: {
      const data = action.payload as FetchStreamChildrenError;
      const id = data.id;
      const stream = state.items[id];

      return {
        ...state,
        items: {
          ...state.items,
          [id]: {
            ...stream,
            errors: data.error,
            loadingChildren: false,
          },
        },
      };
    }
    case INVALIDATE_STREAM_CHILDREN: {
      const streamId = action.payload as string;
      const stream = state.items[streamId];
    
      if (!stream || !stream.item) return state;
    
      return {
        ...state,
        items: {
          ...state.items,
          [streamId]: {
            ...stream,
            fetchedChildren: false,
            loadingChildren: false,
            item: {
              ...stream.item,
              children: [],
            },
            pagination: {
              currentPage: 0,
              totalPages: 0,
              hasMoreItems: false,
            },
          },
        },
      };
    }
    
    case FETCHING_STREAM_ANALYTICS_REQUEST: {
      const data = action.payload;
      const id = data.id;
      const stream = state.items[id] || initialState;

      if (stream.item == null) {
        return state;
      }

      var analytics = stream.item.analytics;

      return {
        ...state,
        items: {
          ...state.items,
          [id]: {
            ...stream,
            item: {
              ...stream.item,
              analytics: {
                ...analytics,
              },
            },
          },
        },
      };
    }

    case FETCHING_STREAM_ANALYTICS_SUCCEEDED: {
      const data = action.payload as FetchStreamAnalyticsResponse;
      const id = data.id;
      const stream = state.items[id];

      if (stream.item == null) {
        return state;
      }

      var analytics = stream.item.analytics;

      return {
        ...state,
        items: {
          ...state.items,
          [id]: {
            ...stream,
            item: {
              ...stream.item,
              analytics: {
                ...analytics,
                item: data,
              },
            },
          },
        },
      } as Stream;
    }

    case FETCHING_STREAM_ANALYTICS_FAILED: {
      const data = action.payload as FetchStreamAnalyticsError;
      const id = data.id;
      var stream = state.items[id];

      if (stream.item == null) {
        return state;
      }

      var analytics = stream.item.analytics;

      return {
        ...state,
        items: {
          ...state.items,
          [id]: {
            ...stream,
            item: {
              ...stream.item,
              analytics: {
                ...analytics,
              },
            },
            errors: action.payload.error,
          },
        },
      };
    }

    case DELETING_STREAM_REQUEST: {
      const data = action.payload;
      const id = data.id;
      var stream = state.items[id];

      return {
        ...state,
        items: {
          ...state.items,
          [id]: {
            ...stream,
            deletion: {
              deleted: false,
              deleting: true,
            },
          },
        },
      };
    }

    case DELETING_STREAM_SUCCEEDED: {
      const { id } = action.payload as DeleteStreamResponse;
      var stream = state.items[id];

      return {
        ...state,
        items: {
          [id]: {
            ...stream,
            deletion: {
              deleted: true,
              deleting: false,
            },
            item: null,
          },
        },
      };
    }

    case DELETING_STREAM_FAILED: {
      const data = action.payload as DeleteStreamError;
      const id = data.id;
      var stream = state.items[id];

      return {
        ...state,
        items: {
          ...state.items,
          [id]: {
            ...stream,
            deletion: {
              deleted: false,
              deleting: false,
              error: data.error,
            },
          },
        },
      };
    }

    case UPDATING_ASSET_REQUEST: {
      const data = action.payload;
      const updatedAssetId = data.request.id;

      if (!data.streamId || !state.items[data.streamId]) {
        return state;
      }

      const updatedChildren = state.items[data.streamId]?.item?.children?.map(
        (child: any) =>
          child.id === updatedAssetId
            ? { ...child, name: data.request.name }
            : child,
      );

      return {
        ...state,
        items: {
          ...state.items,
          [data.streamId]: {
            ...state.items[data.streamId],
            updation: {
              updating: true,
              updated: false,
              error: null,
            },
            item: {
              ...state.items[data.streamId].item,
              children: updatedChildren,
            },
          },
        },
      };
    }

    case UPDATING_ASSET_SUCCEEDED: {
      const data = action.payload as UpdateAssetResponse;
      const updatedAssetId = data.response.id;

      if (!data.streamId || !state.items[data.streamId]) {
        return state;
      }

      const updatedChildren = state.items[data.streamId]?.item?.children?.map(
        (child: any) =>
          child.id === updatedAssetId ? { ...child, ...data.response } : child,
      );

      return {
        ...state,
        items: {
          ...state.items,
          [data.streamId]: {
            ...state.items[data.streamId],
            updation: {
              updating: false,
              updated: true,
              error: null,
            },
            item: {
              ...state.items[data.streamId].item,
              children: updatedChildren,
            },
          },
        },
      } as Stream;
    }

    case UPDATING_ASSET_FAILED: {
      const data = action.payload as UpdateAssetErrorResponse;

      if (!data.streamId || !state.items[data.streamId]) {
        return state;
      }

      return {
        ...state,
        items: {
          ...state.items,
          [data.streamId]: {
            ...state.items[data.streamId],
            updation: {
              updating: false,
              updated: false,
              error: null,
            },
            errors: data.error,
          },
        },
      };
    }

    case UPDATING_GROUP_REQUEST: {
      const data = action.payload;
      const updatedAssetId = data.entity.id;

      if (!state.items[data.streamId]) {
        return state;
      }

      const updatedChildren = state.items[data.streamId]?.item?.children?.map(
        (child: any) =>
          child.id === updatedAssetId
            ? { ...child, name: data.entity.name }
            : child,
      );

      return {
        ...state,
        items: {
          ...state.items,
          [data.streamId]: {
            ...state.items[data.streamId],
            updatingGroup: true,
            item: {
              ...state.items[data.streamId].item,
              children: updatedChildren,
            },
          },
        },
      };
    }

    case UPDATING_GROUP_SUCCEEDED: {
      const data = action.payload as UpdateGroupSucceeded;
      const updatedGroupId = data.group.id;

      if (!state.items[data.streamId]) {
        return state;
      }

      const updatedChildren = state.items[data.streamId]?.item?.children?.map(
        (child: any) =>
          child.id === updatedGroupId ? { ...child, ...data.group } : child,
      );

      return {
        ...state,
        items: {
          ...state.items,
          [data.streamId]: {
            ...state.items[data.streamId],
            updatingGroup: false,
            item: {
              ...state.items[data.streamId].item,
              children: updatedChildren,
            },
          },
        },
      } as Stream;
    }

    case UPDATING_GROUP_FAILED: {
      const data = action.payload as UpdateGroupFailed;

      if (!state.items[data.streamId]) {
        return state;
      }

      return {
        ...state,
        items: {
          ...state.items,
          [data.streamId]: {
            ...state.items[data.streamId],
            updatingGroup: false,
            errors: data.error,
          },
        },
      };
    }

    case FETCHING_ASSET_ANALYTICS_REQUEST: {
      const data = action.payload as FetchAssetAnalyticsRequest;
      const assetId = data.assetId;
      const streamId = data.streamId || "";

      if (!state.items[streamId]) {
        return state;
      }

      return {
        ...state,
        items: {
          ...state.items,
          [streamId]: {
            ...state.items[streamId],
            item: {
              ...state.items[streamId].item,
              children:
                data.streamId &&
                state.items[data.streamId]?.item?.children?.map((child: any) =>
                  child.id === assetId ? { ...child, loading: true } : child,
                ),
            },
          },
        },
      } as Stream;
    }

    case FETCHING_ASSET_ANALYTICS_SUCCEEDED: {
      const data = action.payload as FetchAssetAnalyticsResponse;
      const assetId = data.assetId;
      const streamId = data.streamId || "";

      if (!state.items[streamId]) {
        return state;
      }

      const sortPreference = getStreamSortPreference();

      const updatedChildren = (state.items[streamId]?.item?.children ?? []).map(
        (child: any) =>
          child.id === assetId
            ? { ...child, analytics: data.response }
            : child
      );

      const sortedChildren =
      sortPreference && sortPreference.sortBy !== 'name' && sortPreference.sortBy !== 'modified_date'
        ? sortChildren(sortPreference.sortBy, sortPreference.orderBy, updatedChildren)
        : updatedChildren;

      return {
        ...state,
        items: {
          ...state.items,
          [streamId]: {
            ...state.items[streamId],
            item: {
              ...state.items[streamId].item,
              children: sortedChildren
            },
          },
        },
      } as Stream;
    }

    case SORTING_STREAM: {
      const data = action.payload;

      if (!state.items[data.id]) {
        return state;
      }

      const sortedChildren = sortChildren(
        data.sort.type,
        data.sort.order,
        state.items[data.id]?.item?.children ?? [],
      );

      return {
        ...state,
        items: {
          ...state.items,
          [data.id]: {
            ...state.items[data.id],
            item: {
              ...state.items[data.id].item,
              children: sortedChildren,
            },
            sort: data.sort,
          },
        },
      };
    }

    case DELETING_ASSET_FROM_STREAM_REQUEST: {
      const data = action.payload;
      const streamId = data.id;

      return {
        ...state,
        items: {
          ...state.items,
          [streamId]: {
            ...state.items[streamId],
            assetsDeletion: {
              deleting: true,
              deleted: false,
            },
          },
        },
      };
    }

    case DELETING_ASSET_FROM_STREAM_SUCCEEDED: {
      const data = action.payload as DeleteAssetFromStreamSucceeded;
      const assets = data.response;
      const streamId = data.streamId;

      const assetIds = assets.map((asset: any) => asset.id);
      const updatedChildren = state.items[
        data.streamId
      ]?.item?.children?.filter((child: any) => !assetIds.includes(child.id));

      return {
        ...state,
        items: {
          ...state.items,
          [streamId]: {
            ...state.items[streamId],
            assetsDeletion: {
              deleting: false,
              deleted: true,
            },
            item: {
              ...state.items[streamId].item,
              children: updatedChildren,
            },
          },
        },
      } as Stream;
    }

    case DELETING_ASSET_FROM_STREAM_FAILED: {
      const data = action.payload as DeleteAssetFromStreamFailed;
      const streamId = data.streamId;
      const error = action.payload;

      return {
        ...state,
        items: {
          ...state.items,
          [streamId]: {
            ...state.items[streamId],
            assetsDeletion: {
              deleting: false,
              deleted: false,
            },
            errors: error,
          },
        },
      } as Stream;
    }

    case DELETING_GROUP_REQUEST: {
      const data = action.payload;
      const streamId = data.streamId;

      if (!state.items[data.streamId]) {
        return state;
      }

      return {
        ...state,
        items: {
          ...state.items,
          [streamId]: {
            ...state.items[streamId],
            groupsDeletion: {
              deleting: true,
              deleted: false,
            },
          },
        },
      };
    }

    case DELETING_GROUP_SUCCEEDED: {
      const data = action.payload;
      const groupId = data.group;
      const streamId = data.streamId;

      if (!state.items[data.streamId]) {
        return state;
      }

      const updatedChildren = state.items[
        data.streamId
      ]?.item?.children?.filter((child: any) => child.id !== groupId);

      return {
        ...state,
        items: {
          ...state.items,
          [streamId]: {
            ...state.items[streamId],
            groupsDeletion: {
              deleting: false,
              deleted: true,
            },
            item: {
              ...state.items[streamId].item,
              children: updatedChildren,
            },
          },
        },
      };
    }

    case DELETING_GROUP_FAILED: {
      const data = action.payload;
      const streamId = data.streamId;
      const error = action.payload;

      if (!state.items[data.streamId]) {
        return state;
      }

      return {
        ...state,
        items: {
          ...state.items,
          [streamId]: {
            ...state.items[streamId],
            groupsDeletion: {
              deleting: false,
              deleted: false,
            },
            item: {
              ...state.items[streamId].item,
              error: error,
            },
          },
        },
      };
    }

    case MOVING_ENTITY_SUCCEEDED: {
      const data = action.payload;
      const streamId = data.source.id;
      const entities = data.entities;
      const entityIds = entities.map((entity: any) => entity.id);
      const updatedChildren = state.items[streamId]?.item?.children?.filter(
        (child: any) => !entityIds.includes(child.id),
      );

      if (!state.items[streamId]?.item) {
        return state;
      }

      return {
        ...state,
        items: {
          ...state.items,
          [streamId]: {
            ...state.items[streamId],
            item: {
              ...state.items[streamId].item,
              children: updatedChildren,
            },
          },
        },
      };
    }

    case UPDATING_STREAM_REQUEST: {
      const data = action.payload;
      const streamId = data.request.id;

      return {
        ...state,
        items: {
          ...state.items,
          [streamId]: {
            ...state.items[streamId],
            updation: {
              updating: true,
              updated: false,
              error: null,
            },
          },
        },
      };
    }

    case UPDATING_STREAM_SUCCEEDED: {
      const data = action.payload;
      const streamId = data.id;

      return {
        ...state,
        items: {
          ...state.items,
          [streamId]: {
            ...state.items[streamId],
            item: {
              ...state.items[streamId].item,
              ...data.response,
              children: state.items[streamId]?.item?.children,
            },
            updation: {
              updating: false,
              updated: true,
              error: null,
            },
          },
        },
      };
    }

    case UPDATING_STREAM_FAILED: {
      const data = action.payload;
      const streamId = data.id;

      return {
        ...state,
        items: {
          ...state.items,
          [streamId]: {
            ...state.items[streamId],
            updation: {
              updating: false,
              updated: false,
              error: data.error,
            },
          },
        },
      };
    }

    case STREAM_UPDATE_PROPERTY: {
      const { id, property, value } = action.payload as {
        id: string;
        property: string;
        value: any;
      };

      return {
        ...state,
        items: {
          ...state.items,
          [id]: {
            ...state.items[id],
            edit: {
              [property]: value,
            },
          },
        },
      };
    }

    case ADD_ASSET_TO_STREAM: {
      const data = action.payload as AddAssetToStream;
      const streamId = data.streamId;

      return {
        ...state,
        items: {
          ...state.items,
          [streamId]: {
            ...state.items[streamId],
            creatingAsset: true,
            createdAsset: false,
          },
        },
      };
    }

    case ADD_ASSET_TO_STREAM_SUCCEDDED: {
      const data = action.payload as AddAssetToStreamSucceeded;

      let updatedChildren = [
        ...(state.items[data.streamId]?.item?.children || []),
        ...data.assets,
      ];
      updatedChildren = _.uniqBy(updatedChildren, "id");

      const sortType = state.items.sort
        ? state.items[data.streamId].sort.type
        : "Name";
      const sortOrder = state.items.sort
        ? state.items[data.streamId].sort.order
        : "ASC";

      updatedChildren = sortChildren(sortType, sortOrder, updatedChildren);

      return {
        ...state,
        items: {
          ...state.items,
          [data.streamId]: {
            ...state.items[data.streamId],
            creatingAsset: false,
            createdAsset: true,
            createdGroup: true,
            item: {
              ...state.items[data.streamId].item,
              children: updatedChildren,
              analytics: {
                ...state.items[data.streamId]?.item?.analytics,
                item: {
                  ...state.items[data.streamId]?.item?.analytics?.item,
                  assetCount:
                    (state.items[data.streamId]?.item?.analytics?.item
                      .assetCount || 0) + data.assets.length,
                },
              },
            },
          },
        },
      } as Stream;
    }

    case ADD_ASSET_TO_STREAM_FAILED: {
      const data = action.payload as AddAssetToStreamFailed;

      return {
        ...state,
        items: {
          ...state.items,
          [data.streamId]: {
            ...state.items[data.streamId],
            creatingAsset: false,
            createdAsset: false,
            errors: data.error,
          },
        },
      };
    }

    case CREATING_GROUP_IN_STREAM: {
      const data = action.payload as CreateGroup;

      return {
        ...state,
        items: {
          ...state.items,
          [data.streamId]: {
            ...state.items[data.streamId],
            groupCreation: {
              creating: true,
              created: false,
              item: null,
            },
          },
        },
      };
    }

    case CREATING_GROUP_IN_STREAM_SUCCEEDED: {
      const data = action.payload as CreateGroupSucceeded;

      let updatedChildren = [
        ...(state.items[data.streamId]?.item?.children || []),
        data.group,
      ];
      updatedChildren = _.uniqBy(updatedChildren, "id");

      const sortType = state.items.sort
        ? state.items[data.streamId].sort.type
        : "Name";
      const sortOrder = state.items.sort
        ? state.items[data.streamId].sort.order
        : "ASC";

      updatedChildren = sortChildren(sortType, sortOrder, updatedChildren);

      return {
        ...state,
        items: {
          ...state.items,
          [data.streamId]: {
            ...state.items[data.streamId],
            groupCreation: {
              creating: false,
              created: true,
              item: data.group,
            },
            item: {
              ...state.items[data.streamId].item,
              children: updatedChildren,
            },
          },
        },
      } as Stream;
    }

    case CREATING_GROUP_IN_STREAM_FAILED: {
      const data = action.payload as CreateGroupFailed;

      return {
        ...state,
        items: {
          ...state.items,
          [data.streamId]: {
            ...state.items[data.streamId],
            groupCreation: {
              creating: false,
              created: false,
              item: null,
            },
            errors: data.error,
          },
        },
      };
    }

    case CREATING_STREAM: {
      return {
        ...state,
        creation: {
          ...state.creation,
          creating: true,
          created: false,
          error: null,
        },
      };
    }

    case CREATING_STREAM_SUCCEEDED: {
      const response = action.payload as StreamType;

      return {
        ...state,
        creation: {
          item: response,
          creating: false,
          created: true,
          error: null,
        },
      };
    }

    case CREATING_STREAM_FAILED: {
      const error = action.payload as CreateStreamError;

      return {
        ...state,
        creation: {
          ...state.creation,
          creating: false,
          created: false,
          error: error,
        },
      };
    }

    case INVALIDATE_STREAM_CREATION: {
      return {
        ...state,
        creation: {
          item: null,
          creating: false,
          created: false,
          error: null,
        },
      };
    }

    case REPLACE_SIMILAR_ASSET_SUCCEEDED: {
      const response = action.payload as ReplaceSimilarAssetResponse;

      const newAssetId = response.newAssetId;
      const existingAsset = response.existingAsset;
      const streams = response.streams;

      if (_.size(streams) > 0) {
        const updatedItems = { ...state.items };

        Object.keys(state.items).forEach((id) => {
          const stream = state.items[id];

          if (!stream || !stream?.item?.children) {
            return;
          }

          const newAsset = _.find(stream.item.children, (asset) => {
            return asset.id === newAssetId;
          });

          if (newAsset) {
            let assets = stream.item.children.map((asset) => {
              if (asset.id === newAssetId) {
                return existingAsset;
              }
              return asset;
            });

            assets = _.uniqBy(assets, "id");

            updatedItems[id] = {
              ...stream,
              item: {
                ...stream.item,
                children: assets,
              },
            };
          }
        });

        return {
          ...state,
          items: updatedItems,
        };
      }

      return {
        ...state,
      };
    }
    
    case UPDATING_STREAM_ACCESS_SUCCEDDED: {
      var id = action.payload.id;
      var stream = state.items[id];

      if (!stream) {
        return state;
      }
      return {
        ...state,
        items: {
          ...state.items,
          [id]: {
            ...stream,
            item: {
              ...stream.item,
              readAll: action.payload.response.readAll,
              writeAll: action.payload.response.writeAll,
            },
          },
        },
      };
    }

    default: {
      return state;
    }
  }
}